import className from 'classnames';
import React, { useRef, useState, useEffect } from 'react';
import { IoMdCheckboxOutline } from "react-icons/io";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import TooltipDictionary from '../../../tools/TooltipDictionary';

interface ButtonComponentProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    tipSearchTerm: string;
    children: React.ReactNode;
    toggleValue: boolean;
    primary?: boolean;
    secondary?: boolean;
    subtle?: boolean;
    solo?: boolean;
    left?: boolean;
    center?: boolean;
    right?: boolean;
    lefttoright?: boolean
}

function ButtonToggleTooltip(props: ButtonComponentProps) {
    const {
        tipSearchTerm,
        children,
        toggleValue,
        primary,
        secondary,
        subtle,
        solo,
        left,
        center,
        right,
        lefttoright,
        ...rest
    } = props;

    const tooltipRef = useRef<HTMLSpanElement>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

    const classes = className(
        rest.className,
        'font-face-montserrat-medium text-sm flex items-center px-3 relative group',
        {
            'mb-3 border border-black bg-black text-white': primary,
            'mb-3 border-2 border-black bg-white text-black': secondary,
            'border hover:text-black': subtle,
            'border-gray-500 text-black': toggleValue,
            'border-gray-400 text-gray-400': !toggleValue,
            'rounded-lg py-1.5': solo,
            'rounded-l-lg': left,
            '': center,
            'rounded-r-lg': right,
            'rounded-lg': lefttoright,
        }
    );

    const renderedIcon = (toggleValue)
        ? <IoMdCheckboxOutline />
        : <MdCheckBoxOutlineBlank />

    const buttonText = tipSearchTerm;
    const tooltipText = TooltipDictionary.getTipByName(buttonText);

    // Adjust tooltip position dynamically
    useEffect(() => {
        const updateTooltipPosition = () => {
            const tooltipElement = tooltipRef.current;
            if (tooltipElement) {
                // Temporarily make the tooltip visible to calculate its position
                tooltipElement.style.visibility = 'hidden';
                tooltipElement.style.display = 'block';

                const rect = tooltipElement.getBoundingClientRect();
                const viewportWidth = window.innerWidth;

                let left = '50%';
                let transform = 'translateX(-50%)';

                // If the tooltip overflows on the left
                if (rect.left < 0) {
                    left = '0'; // Align tooltip to the left edge of the viewport
                    transform = 'translateX(0)';
                }

                // If the tooltip overflows on the right
                if (rect.right > viewportWidth) {
                    left = '100%'; // Align tooltip to the right edge of the button
                    transform = 'translateX(-100%)';
                }

                setTooltipStyle({ left, transform });

                // Restore the tooltip's original visibility
                tooltipElement.style.visibility = '';
                tooltipElement.style.display = '';
            }
        };

        // Initial calculation after the component is mounted
        updateTooltipPosition();

        // Recalculate on window resize
        const handleResize = () => {
            updateTooltipPosition();
        };

        window.addEventListener('resize', handleResize);

        // Cleanup event listener on unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []); // Run only once after the component is mounted

    useEffect(() => {
        // Recalculate tooltip position when tooltipText changes
        const updateTooltipPosition = () => {
            const tooltipElement = tooltipRef.current;
            if (tooltipElement) {
                tooltipElement.style.visibility = 'hidden';
                tooltipElement.style.display = 'block';

                const rect = tooltipElement.getBoundingClientRect();
                const viewportWidth = window.innerWidth;

                let left = '50%';
                let transform = 'translateX(-50%)';

                if (rect.left < 0) {
                    left = '0';
                    transform = 'translateX(0)';
                }

                if (rect.right > viewportWidth) {
                    left = '100%';
                    transform = 'translateX(-100%)';
                }

                setTooltipStyle({ left, transform });

                tooltipElement.style.visibility = '';
                tooltipElement.style.display = '';
            }
        };

        updateTooltipPosition();
    }, [tooltipText]); // Recalculate when tooltipText changes

    return (
        <button {...rest} className={classes}>
            {renderedIcon}
            &nbsp;
            {children}
            {/* Tooltip */}
            {tooltipText && (
                <span
                    ref={tooltipRef}
                    className="absolute bottom-full mb-2 hidden group-hover:flex bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap"
                    style={{
                        ...tooltipStyle,
                        position: 'absolute',
                        maxWidth: 'calc(100vw - 20px)', // Prevent tooltip from extending outside the viewport
                    }}
                >
                    {tooltipText}
                </span>
            )}
        </button>
    );
}

export default ButtonToggleTooltip;
